import * as React from "react";
import { view } from "@orderofchaos/mvvm";
import { Layout } from "../../components/Layout/Layout";
import FieldSet from "../../components/FieldSet/FieldSet";
import Input from "../../components/Input/Input";
import Button, {
  ButtonDesign,
  ButtonSize,
} from "../../components/Button/Button";
import styles from "./QRManagementPage.module.css";
import { QRManagementPageViewModel } from "./QRManagementPageViewModel";

interface QRManagementPageProps {}

export const QRManagementPage: React.FC<QRManagementPageProps> = view(
  QRManagementPageViewModel,
)(({
  viewModel: {
    codes,
    requestedCountInput,
    selectedPreset,
    exportPreset,
    sheetPresetOptions,
    normalizedGenerationCount,
    generationSheetsCount,
    totalCount,
    exportSheetsCount,
    canGenerateBatch,
    canExport,
    operationError,
    operationMessage,
    onRequestedCountChange,
    onSheetPresetChange,
    generateBatch,
    exportAsPdf,
  },
}) => {
  return (
    <Layout title="экспорт QR-кодов">
      <div className={styles.root}>
        <section className={styles.introCard}>
          <h2 className={styles.introTitle}>
            Как пользоваться экраном печати QR
          </h2>
          <p className={styles.introText}>
            Этот экран нужен, чтобы быстро подготовить новую партию QR-кодов для
            печати на листах A4.
          </p>
          <p className={styles.introText}>
            Вы выбираете размер QR (через формат листа), указываете нужное
            количество и получаете готовый PDF для печати.
          </p>
          <ol className={styles.guideSteps}>
            <li>Выберите формат: чем меньше кодов на листе, тем крупнее QR.</li>
            <li>Введите нужное количество кодов.</li>
            <li>Нажмите «Сгенерировать новые QR-коды».</li>
            <li>
              Нажмите «Экспорт в PDF» и распечатайте файл или сохраните его для
              печати позже.
            </li>
          </ol>
        </section>

        <FieldSet title="1) генерация новой партии">
          <div className={styles.formGrid}>
            <label className={styles.fieldLabel} htmlFor="qr-a4-format">
              Формат листа
            </label>
            <select
              id="qr-a4-format"
              className={styles.select}
              value={selectedPreset.key}
              onChange={onSheetPresetChange}
              aria-label="Формат печатного листа A4"
            >
              {sheetPresetOptions.map((preset) => (
                <option key={preset.key} value={preset.key}>
                  {preset.label}
                </option>
              ))}
            </select>

            <label className={styles.fieldLabel} htmlFor="qr-requested-count">
              Нужное количество QR-кодов
            </label>
            <Input
              id="qr-requested-count"
              type="number"
              min={1}
              max={2400}
              value={requestedCountInput}
              onChange={onRequestedCountChange}
              placeholder="например, 20"
            />
          </div>

          <p className={styles.summary}>
            Будет сгенерировано: <strong>{normalizedGenerationCount}</strong> (
            {generationSheetsCount} лист(ов) по {selectedPreset.codesPerSheet})
          </p>

          <Button
            design={ButtonDesign.primary}
            size={ButtonSize.medium}
            onClick={generateBatch}
            disabled={!canGenerateBatch}
          >
            сгенерировать новые QR-коды
          </Button>
        </FieldSet>

        <FieldSet title="2) экспорт в PDF для печати">
          <p className={styles.summary}>
            Готово к печати: <strong>{totalCount}</strong> кодов (
            {exportSheetsCount} лист(ов), формат {exportPreset.label})
          </p>

          <Button
            design={ButtonDesign.secondary}
            size={ButtonSize.medium}
            onClick={exportAsPdf}
            disabled={!canExport}
          >
            экспорт в PDF
          </Button>
        </FieldSet>

        {operationError && <p className={styles.error}>{operationError}</p>}
        {operationMessage && (
          <p className={styles.message}>{operationMessage}</p>
        )}

        <section className={styles.noteCard}>
          <h3 className={styles.noteTitle}>Важно знать</h3>
          <p className={styles.noteText}>
            Количество автоматически округляется вверх до полного листа
            выбранного формата. Так печать и нарезка наклеек всегда проходят без
            «пустых» мест в макете.
          </p>
          <p className={styles.noteText}>
            Этот экран подготавливает только новые QR-коды. Привязка QR к товару
            или коробке выполняется на экранах создания и редактирования
            соответствующей сущности.
          </p>
          {codes.length === 0 && (
            <p className={styles.noteHint}>
              Сначала сгенерируйте партию, затем экспортируйте PDF для печати.
            </p>
          )}
        </section>
      </div>
    </Layout>
  );
});
